// Можно ли продлить назначение — по тому, что карточка человека уже знает.
//
// Кнопка «Продлить» во вкладке «Назначения» и панель продления решают это ОДНИМ правилом: на
// закрытом объекте, снятом посту и у уволенного, кому продлевать некуда, сервер откажет
// гарантированно. Кнопка, ведущая в такой отказ, — та же ошибка, из-за которой 19.10.2026 убрали
// «Перевести» и «Закрыть» у назначений с последним днём. Правило повторяет серверное
// (`AssignmentsService._продлить`): уволенного продлевают не дальше дня увольнения, закрытое не
// оживляют. Чего карточка не знает (старый сервер без признаков объекта и поста), то не решаем:
// скажет сервер.

import type { AssignmentOut, EmployeeOut } from '@/shared/api/endpoints/people';
import { formatDateOnly } from '@/shared/time/format';

/** Объект закрыт или пост снят: продлевать на нём нельзя. Пусто — действует или не знаем. */
export function закрытоМесто(назначение: AssignmentOut): string | null {
  if (назначение.site_active === false) {
    return `Объект «${назначение.site_name}» закрыт: продлевать на нём нельзя. Чтобы человек работал дальше, поставьте его на пост действующего объекта.`;
  }
  if (назначение.post_active === false) {
    return `Пост «${назначение.post_name}» на объекте «${назначение.site_name}» снят: продлевать на нём нельзя. Чтобы человек работал дальше, поставьте его на действующий пост.`;
  }
  return null;
}

/**
 * Уволенный, кому продлевать некуда: увольнение не позже последнего дня назначения (или даты
 * увольнения нет). Смотрим на СОСТОЯНИЕ, как и сервер: при возврате из уволенных дата
 * увольнения остаётся, и по ней одной вернувшийся считался бы уволенным.
 */
export function уволенБезЗапаса(
  назначение: AssignmentOut,
  сотрудник: EmployeeOut | undefined,
): string | null {
  if (сотрудник?.status !== 'fired' || назначение.date_to === null) {
    return null;
  }
  if (сотрудник.fired_at === null) {
    return 'Человек уволен: продлевать назначение некуда. Если он вернулся, смените ему состояние в карточке.';
  }
  if (сотрудник.fired_at <= назначение.date_to) {
    return `Человек уволен ${formatDateOnly(сотрудник.fired_at)}, назначение кончается ${formatDateOnly(назначение.date_to)}: продлевать некуда. Если он вернулся, смените ему состояние в карточке.`;
  }
  return null;
}

/**
 * Последний рабочий день уволенного, если он ПОЗЖЕ конца назначения: дальше него продлевать
 * нельзя, а до него — можно и нужно (уволен 15.10, назначение по 30.09: октябрьские дни ему
 * оплачиваются). Пусто — не уволен либо продлевать некуда.
 */
export function пределУвольнения(
  назначение: AssignmentOut | undefined,
  сотрудник: EmployeeOut | undefined,
): string | null {
  if (назначение === undefined || назначение.date_to === null) {
    return null;
  }
  if (сотрудник?.status !== 'fired' || сотрудник.fired_at === null) {
    return null;
  }
  return сотрудник.fired_at > назначение.date_to ? сотрудник.fired_at : null;
}

/** Почему продлить нельзя совсем. Пусто — можно или неизвестно (тогда решит сервер). */
export function почемуНельзяПродлить(
  назначение: AssignmentOut | undefined,
  сотрудник: EmployeeOut | undefined,
): string | null {
  if (назначение === undefined) {
    return null;
  }
  if (назначение.date_to === null) {
    return 'Назначение и так без даты окончания: продлевать нечего. Последний день ставит кнопка «Закрыть».';
  }
  return закрытоМесто(назначение) ?? уволенБезЗапаса(назначение, сотрудник);
}
